import type { LiveResponse, LiveVehicle, Motion, VehiclePlan, VehicleState, VehicleStatus } from './types';
import type { VehicleType } from '../vehicles/useVehicleDetail';
import { normalizeVehicleState } from './vehicleState';

/**
 * Last-known live data, persisted on the device so a COLD launch while offline
 * still shows the fleet's last-known positions instead of a blank error (PLAN
 * §Offline — "last-known state with a visible age").
 *
 * WHY THIS IS SAFE against the "a PWA cached map looks live" lie:
 *   - It is stored with the wall-clock instant it was saved (`savedAt`), which
 *     the offline banner and the vehicle panels render as an absolute "as of
 *     HH:MM" — never a relative age recomputed against a clock we do not trust.
 *   - It seeds the query as `initialData` with `initialDataUpdatedAt = savedAt`,
 *     which is OLDER than the 30 s stale time, so online the query refetches
 *     immediately and the seed is replaced by live data. It is a fallback, never
 *     presented as current: the map's offline treatment forces every seeded
 *     vehicle to past-tense while we are offline.
 *
 * WHY IN localStorage, NOT THE SERVICE-WORKER CACHE: the SW must not cache API
 * responses as if they were app shell (PLAN §SW discipline). This is the tenant's
 * own last-known fleet, on the tenant's own device, kept out of the SW entirely.
 *
 * TENANT ISOLATION — the load-bearing guard (CLAUDE.md §Tenant isolation):
 *   The blob is keyed by the authenticated principal (`raad.live.lastKnown.<id>`)
 *   AND carries that principal inside it, verified on read. `readPersistedLive`
 *   takes the CURRENT principal and only ever NAMES that principal's key, so a
 *   blob written by a different tenant lives under a key we never construct and
 *   is structurally unreadable — the same discipline as slice 06's cache keys,
 *   where a foreign key is never named. This does NOT rely on a clear having
 *   fired: the dangerous path (A's token silently expires, B logs in on the same
 *   device with no logout event) never fires a clear, so namespacing is the
 *   guard that runs. `clearPersistedLive` (sign-out / forced logout / login /
 *   boot-to-unauthenticated) is defence in depth on top of it, and also evicts
 *   any legacy un-namespaced blob left by an earlier build.
 *
 * SIZE — a bounded projection, not the whole response (review SHOULD FIX):
 *   Only the fields the last-known seed renders are stored (below), and the
 *   vehicle count is capped at `MAX_PERSISTED_VEHICLES`, located vehicles first.
 *   The old code serialised the entire `LiveResponse`; at thousands of vehicles
 *   that overran the ~5 MB `localStorage` quota, the `catch` swallowed the throw,
 *   and the feature silently died for exactly the large tenants it is sold to.
 */

const KEY_PREFIX = 'raad.live.lastKnown';

/**
 * Cap on persisted vehicles. A projected row is ~250–300 bytes of JSON, so 1000
 * rows is ~0.3 MB — comfortably inside the ~5 MB origin quota with room for the
 * rest of localStorage, while covering the vast majority of fleets. The seed is a
 * cold-launch convenience replaced by live data within seconds online, and the
 * map clusters above ~200 markers anyway, so a larger fleet loses nothing it could
 * usefully show from an offline snapshot. Located vehicles are kept first because
 * only they draw a marker; the remainder fills the list until the cap.
 */
export const MAX_PERSISTED_VEHICLES = 1000;

function storageKey(principal: string): string {
  return `${KEY_PREFIX}.${principal}`;
}

/** The bounded per-vehicle projection actually written to disk. */
interface PersistedVehicle {
  id: string;
  plate: string;
  label: string | null;
  plan: VehiclePlan;
  vehicleType: VehicleType;
  status: VehicleStatus;
  state: VehicleState;
  /** 15e — persisted so a cold start can still choose engineStale's sub-line. */
  lastFixMoving: boolean | null;
  motion: Motion;
  lat: number | null;
  lon: number | null;
  speedKmh: number;
  course: number | null;
  lastMovingCourse: number | null;
  positionFixedAt: string | null;
  positionAgeSec: number | null;
  /**
   * 18g — persisted so an offline cold start does not claim a tracker is fitted
   * when the last thing we saw said it was not.
   *
   * 🔴 ADDITIVE, AND OLD BLOBS ARE THE REASON THE DEFAULT IS `true`. Every
   * snapshot already sitting in a customer's localStorage was written without
   * this key. `expand` reads an absent value as `true` — see its note — because
   * the other direction would paint a whole cached fleet "No tracker" on the
   * first offline open after this ships. A boolean is safe to add here in a way
   * a new `VehicleState` or `Motion` member would NOT be: those are read by
   * exhaustive `Record<…>` maps, and a stored value outside the union has no
   * bounded behaviour.
   */
  hasTracker: boolean;
}

interface PersistedEnvelope {
  /** The authenticated principal this blob belongs to. Verified on read. */
  principal: string;
  /** Device wall-clock ms at save time. Rendered as an absolute "as of HH:MM". */
  savedAt: number;
  data: {
    vehicles: PersistedVehicle[];
    liveness: LiveResponse['liveness'];
    serverTime: string;
  };
}

export interface PersistedLive {
  data: LiveResponse;
  /** Device wall-clock ms at save time. Rendered as an absolute "as of HH:MM". */
  savedAt: number;
}

function project(v: LiveVehicle): PersistedVehicle {
  return {
    id: v.id,
    plate: v.plate,
    label: v.label,
    plan: v.plan,
    vehicleType: v.vehicleType,
    status: v.status,
    state: v.state,
    lastFixMoving: v.lastFixMoving,
    motion: v.motion,
    lat: v.lat,
    lon: v.lon,
    speedKmh: v.speedKmh,
    course: v.course,
    lastMovingCourse: v.lastMovingCourse,
    positionFixedAt: v.positionFixedAt,
    positionAgeSec: v.positionAgeSec,
    hasTracker: v.hasTracker,
  };
}

/**
 * Expand a projected row back to a full `LiveVehicle`. The dropped fields are
 * diagnostic or non-rendering and take honest defaults: the seed is last-known
 * data, so `stale: true` is the truthful state (LiveMap forces it anyway), and
 * `cached: true` reflects that it came off the device.
 */
function expand(p: PersistedVehicle): LiveVehicle {
  return {
    ...p,
    // Guard old blobs written before `state` was persisted (13e): an absent value
    // normalises to `unknown` rather than leaving the field missing.
    state: normalizeVehicleState((p as { state?: unknown }).state),
    // 15e — old blobs predate this field. NULL, not a guess: null means "no
    // sub-line", which is the honest reading of a value we never stored.
    lastFixMoving: (p as { lastFixMoving?: boolean | null }).lastFixMoving ?? null,
    // 18g — old blobs predate this field. `true`, NOT `false`: absent means "we
    // did not record it", and reading that as "no tracker" would label every
    // vehicle in a cached fleet as trackerless on the first offline open after
    // this ships. The live fetch corrects it moments later either way, so the
    // default is chosen by which mistake is worse to show.
    hasTracker: (p as { hasTracker?: boolean }).hasTracker !== false,
    model: null,
    trackerContactAt: null,
    freshnessDiverged: false,
    cacheAgeSec: null,
    cached: true,
    stale: true,
    // Tracker health is volatile telemetry, not persisted in the last-known seed
    // (like `trackerContactAt`/`cacheAgeSec`). `null` = "no telemetry" is the
    // honest state for an offline seed; the live fetch/socket refills it on arrival.
    trackerHealth: null,
    // 13f — the silent-since instant is derived from the server sweep, not stored
    // in the seed. Null here (no silent-since line) until the live fetch supplies
    // it moments later, exactly like `trackerHealth` above.
    trackerSilentSince: null,
    // SLICE B — deliberately NOT persisted (see `project`), and it expands to
    // `false`, not to a retained value. By the same "which mistake is worse" test
    // `hasTracker` above is decided by, and it lands the opposite way: a seed is
    // last-known data of unknown age, so restoring `true` would open the map with a
    // red marker claiming a vehicle is speeding RIGHT NOW on the strength of a
    // reading that could be hours old — a false alarm on a safety signal, and one
    // the customer cannot dismiss. `false` can only fail to show a red for the
    // moment before the live fetch arrives and corrects it.
    speeding: false,
  };
}

/**
 * Read the current principal's last-known snapshot, or null. A blob is returned
 * ONLY when its embedded `principal` matches the caller's — and only the caller's
 * own key is ever read — so another tenant's snapshot is unreachable by
 * construction. An absent principal (e.g. before login) returns null.
 */
export function readPersistedLive(principal: string): PersistedLive | null {
  if (!principal) return null;
  try {
    const raw = localStorage.getItem(storageKey(principal));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      (parsed as PersistedEnvelope).principal !== principal ||
      typeof (parsed as PersistedEnvelope).savedAt !== 'number' ||
      typeof (parsed as PersistedEnvelope).data !== 'object' ||
      (parsed as PersistedEnvelope).data === null ||
      !Array.isArray((parsed as PersistedEnvelope).data.vehicles)
    ) {
      return null;
    }
    const env = parsed as PersistedEnvelope;
    return {
      savedAt: env.savedAt,
      data: {
        vehicles: env.data.vehicles.map(expand),
        liveness: env.data.liveness ?? { degraded: false },
        serverTime: env.data.serverTime,
      },
    };
  } catch {
    // Private mode, quota, or corrupt JSON — no seed is not an error.
    return null;
  }
}

/**
 * Persist the current principal's last-known snapshot under its own key. Located
 * vehicles are kept first, then the list is capped at `MAX_PERSISTED_VEHICLES`,
 * and only the projected fields are stored — so a large fleet stays well inside
 * the localStorage quota instead of silently failing to persist.
 */
export function writePersistedLive(principal: string, data: LiveResponse, savedAt: number): void {
  if (!principal) return;
  try {
    const located = data.vehicles.filter((v) => v.lat != null && v.lon != null);
    const unlocated = data.vehicles.filter((v) => v.lat == null || v.lon == null);
    const vehicles = [...located, ...unlocated].slice(0, MAX_PERSISTED_VEHICLES).map(project);
    const env: PersistedEnvelope = {
      principal,
      savedAt,
      data: { vehicles, liveness: data.liveness, serverTime: data.serverTime },
    };
    localStorage.setItem(storageKey(principal), JSON.stringify(env));
  } catch {
    /* quota / private mode — a seed we cannot persist is not an error */
  }
}

/**
 * Remove EVERY persisted last-known blob, whichever principal owns it, plus any
 * legacy un-namespaced blob from an earlier build. Called on sign-out, forced
 * logout, at the top of login (before minting the next session) and when boot
 * lands on unauthenticated — defence in depth on top of the per-principal key.
 */
export function clearPersistedLive(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && (k === KEY_PREFIX || k.startsWith(`${KEY_PREFIX}.`))) toRemove.push(k);
    }
    for (const k of toRemove) localStorage.removeItem(k);
  } catch {
    /* nothing to clear if storage is unavailable */
  }
}
